import { useState, useCallback } from 'react';
import {
  CampaignPerformanceItem,
  GlobalPerformanceItem,
  PerformanceFilterInput,
} from '../types/performance.types';
import {
  getCampaignPerformance,
  getGlobalPerformance,
} from '../services/performanceService';

interface UsePerformanceResult<T> {
  data: T[];
  loading: boolean;
  error: Error | null;
  refetch: (filter: PerformanceFilterInput) => Promise<void>;
}

/**
 * Hook to fetch campaign performance data
 */
export const useCampaignPerformance = (): UsePerformanceResult<CampaignPerformanceItem> => {
  const [data, setData] = useState<CampaignPerformanceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async (filter: PerformanceFilterInput) => {
    setLoading(true);
    try {
      const result = await getCampaignPerformance(filter);
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'));
      console.error('Error fetching campaign performance:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, refetch };
};

/**
 * Hook to fetch global performance data
 */
export const useGlobalPerformance = (): UsePerformanceResult<GlobalPerformanceItem> => {
  const [data, setData] = useState<GlobalPerformanceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async (filter: PerformanceFilterInput) => {
    setLoading(true);
    try {
      const result = await getGlobalPerformance(filter);
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'));
      console.error('Error fetching global performance:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, refetch };
};
